import { LOCAL_STORAGE } from '@/lib/config';
import { UserRole } from '@/lib/db-row-types';
import { SSO_WORKSPACE_MANAGED_MESSAGE } from '@/utils/auth/sso-copy';
import { getBrowserClient } from '@/utils/supabase/client';
import { fetchCurrentUser } from '@/utils/supabase/current-user';

// Feature-flag definitions live in a browser-free module so server routes can reuse the
// same allowlist without importing the browser Supabase client. Re-exported here for
// backwards-compatible import paths.
export {
  WORKSPACE_FEATURES,
  WORKSPACE_FEATURE_META,
  isKnownWorkspaceFeature,
  isWorkspaceFeatureEnabled,
} from './workspace-features';
export type { WorkspaceFeature } from './workspace-features';

/**
 * Fetches all workspaces the current user has access to
 */
export async function fetchUserWorkspaces() {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('workspaces')
    .select('*')
    .order('name', { ascending: true });

  return { data, error };
}

/**
 * Sets the default workspace for the current user (stored in user_profiles)
 * Note: This requires adding a default_workspace_id field to user_profiles table
 */
export async function setDefaultWorkspace(workspaceId: string) {
  const user = await fetchCurrentUser();

  if (!user) {
    return { data: null, error: 'User not authenticated' };
  }

  if (typeof window !== 'undefined') {
    localStorage.setItem(LOCAL_STORAGE.WORKSPACE_ID, workspaceId);
  }

  return { data: { workspaceId }, error: null };
}

/**
 * Gets the default workspace for the current user
 */
export function getDefaultWorkspace() {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(LOCAL_STORAGE.WORKSPACE_ID);
  }
  return null;
}

export async function createWorkspace({ name }: { name: string }) {
  const supabase = getBrowserClient();

  const user = await fetchCurrentUser();
  if (!user) {
    return { data: null, error: 'User not authenticated' };
  }

  const { data, error } = await supabase.from('workspaces').insert({ name }).select().single();

  if (error && error.message?.includes('row-level security')) {
    // The insert policy also excludes anyone whose organization signs in through SSO. Refreshing
    // and signing in again would not help them, so say what actually happened.
    const { data: ssoPolicy } = await supabase.rpc('get_current_user_sso_policy');
    if (ssoPolicy?.[0]?.provider) {
      return { data: null, error: SSO_WORKSPACE_MANAGED_MESSAGE };
    }

    return {
      data: null,
      error: 'Permission denied. Please try refreshing the page and logging in again.',
    };
  }

  return { data, error };
}

export async function getWorkspaceUsers(workspaceId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('list_workspace_members')
    .select('*')
    .eq('workspace_id', workspaceId);
  return { data, error };
}

export async function getUserRoleInWorkspace(workspaceId: string) {
  const supabase = getBrowserClient();
  const user = await fetchCurrentUser();

  if (!user?.id) return null;

  const { data: userRole } = await supabase
    .from('workspace_users')
    .select('role')
    .eq('user_id', user.id)
    .eq('workspace_id', workspaceId)
    .single();

  return userRole?.role ?? null;
}

export async function changeUserRole({
  userId,
  role,
  workspaceId,
}: {
  userId: string;
  role: UserRole;
  workspaceId: string;
}) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('workspace_users')
    .update({ role })
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .select('*')
    .single();
  return { data, error };
}

export async function deleteWorkspaceById(workspaceId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase.from('workspaces').delete().eq('id', workspaceId);
  return { data, error };
}

interface RemoveUserFromWorkspaceArgs {
  userId: string;
  workspaceId: string;
}

export async function removeUserFromWorkspace({
  userId,
  workspaceId,
}: RemoveUserFromWorkspaceArgs) {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('workspace_users')
    .delete()
    .eq('user_id', userId)
    .eq('workspace_id', workspaceId);
  return { data, error };
}
